import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Search, MapPin, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";
import { getImageUrl } from "@/utils/imageUrl";

function TeamCard({ team }) {
  return (
    <Link to={`/deportes/equipo/${team.slug}`}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="bg-white rounded-2xl p-6 shadow-sm border hover:shadow-lg transition-all"
      >
        <div className="flex flex-col items-center text-center">
          {team.logo_url ? (
            <img 
              src={getImageUrl(team.logo_url)} 
              alt={team.name}
              className="w-24 h-24 object-contain mb-4"
            />
          ) : (
            <div className="w-24 h-24 bg-gray-100 rounded-full mb-4 flex items-center justify-center">
              <Users className="h-12 w-12 text-gray-400" />
            </div>
          )}
          
          <h3 className="font-heading font-bold text-lg text-brand-text mb-1">
            {team.name}
          </h3>
          
          {team.short_name && (
            <Badge variant="secondary" className="mb-2">{team.short_name}</Badge>
          )}
          
          {team.city && (
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {team.city}
            </p>
          )}
        </div>
      </motion.div>
    </Link>
  );
}

export default function EquiposPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/sports/teams?is_active=true`);
      setTeams(response.data.items || []);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTeams = teams.filter((team) =>
    team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <PageHero
        section="deportes"
        title="Equipos"
        subtitle="Todos los equipos de la Liga Nacional de Honduras"
        badge="Liga Nacional"
        badgeIcon={Users}
      />

      {/* Search */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            <Link 
              to="/deportes"
              className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a Deportes
            </Link>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar equipo..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Teams Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : filteredTeams.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No hay equipos</h3>
              <p className="text-gray-500">Los equipos aparecerán aquí</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {filteredTeams.map((team) => (
                <TeamCard key={team.team_id} team={team} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
